import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom'; // Import useLocation
import { Chart } from '@domoinc/domo-phoenix';
import ChartDetails from './details';
import DetailsHeader from './details_header';
import DetailsNav from './details_nav';
import ChartFooter from './chart_footer';
import DataTableAccordion from './data_table_accordion';
import Audit from '../utils/audit_util';
import DrillFunctions from './drill_util';
import DateFunctions from './date_util';
import { dataCharts } from '../data/data_charts';
import BreadcrumbsNav from '../dashboards/bread_crumps_nav';

const DetailsLoader = (props) => {
    const { id } = useParams();
    const location = useLocation();
    const detailsHeight = 700;
    const detailsWidth = 1200;

    // Get dashboardId from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const dashboardId = queryParams.get('dashboardId');

    const [data, setData] = useState([]);
    const [options, setOptions] = useState({
        height: 0,
        width: 0,
        animate: true
    });
    const [definition, setDefinition] = useState({
        display_name: "loading...",
        chart_type: "badge_vert_bar"
    });
    const [isLoaded, setIsLoaded] = useState(false);
    const [isDrill, setIsDrill] = useState(false);
    const [drillText, setDrillText] = useState('');
    const [updatedText, setUpdatedText] = useState('');
    const [chart, setChart] = useState(null);
    const [chartId, setChartId] = useState(0);
    const [parentId, setParentId] = useState(0);
    const [drills, setDrills] = useState([]);

    useEffect(() => {
        if (id) {
            const chartId = parseInt(id, 10); 
            if (chartId > 0) {
                loadDetailChart(chartId);
            }
        }
    }, [id]); 

    const loadChartPicker = () => {
        if (chart.isChartPickerOpen()) {
            chart.hideChartPicker();
            Audit.LogUserAction('chart.details.hide.chart.picker', 'details', 'hide');
        } else {
            chart.showChartPicker();
            Audit.LogUserAction('chart.details.show.chart.picker', 'details', 'open');
        }
    };

    const loadChartData = async (chartId, drill = null) => {
        let result;
    
        if (drill) {
            result = await dataCharts.loadChartDrill(chartId, drill.value);
            setIsDrill(true); 
            setParentId(chartId); 
        } else {
            result = await dataCharts.loadChart(chartId);
            setIsDrill(false); 
        }
    
        const drillOutput = DrillFunctions.GetDrillText(result.drills.rows);
    
        const updatedOptions = {
            ...result.options,
            height: detailsHeight,
            width: detailsWidth
        };
        const newChart = new Chart(
            result.definition.chart_type,
            result.data,
            updatedOptions
        );
    
        setData(result.data);
        setOptions(updatedOptions);
        setDefinition(result.definition);
        setDrills(result.drills);
        setChartId(chartId);
        setDrillText(drillOutput);
        
        const updateText = result.latest && result.latest.latestDate 
            ? DateFunctions.GenerateUpdateText(result.latest.latestDate)
            : 'Latest update time not available';
        setUpdatedText(updateText);
        Audit.LogUserAction('chart.details.render', 'details', result.definition.chart_type);
        
        setChart(newChart);
        setIsLoaded(true);
    };
          
    const loadDetailChart = async (chartId) => {
        if (chartId > 0) {
            await loadChartData(chartId);
        }
    };
    
    const handleProcessDrill = async (drill) => {
        await loadChartData(drill.chart_id, drill);
    };

    const handleDetermineDrill = (filter, chartId) => {
        let drillExists = false;
        let drill = {};
        for (let i = 0; i < drills.rows.length; i++) {
            if (drills.rows[i].drill_column === filter.column) {
                drill = drills.rows[i];
                drillExists = true;
                break;
            }
        }
        if (drillExists) {
            if (drill.drill_type === 'chart') {
                drill.value = filter.values[0];
                handleProcessDrill(drill);
            } else if (drill.drill_type === 'link') {
                const url = drill.drill_link.replace("*", filter.values[0]);
                const win = window.open(url, '_blank');
                win.focus();
            }
        }
    };

    const handleLoadParent = (parent) => {
        Audit.LogUserAction('details.drill.return', 'details', parent);
        loadDetailChart(parent);
    };

    const setChartSize = () => {
        setOptions((prevOptions) => ({
            ...prevOptions,
            height: detailsHeight,
            width: detailsWidth
        }));
    };

    const loadChart = () => {
        const newChart = new Chart(
            definition.chart_type,
            data,
            options
        );
        setChart(newChart);
        setIsLoaded(true);
    };

    return (
        <div className="row pt-3 pb-3 pl-3 pr-3">
            <div className="col col-chart-position">
                {dashboardId && <BreadcrumbsNav chartTitle={definition.display_name} dashboardId={dashboardId} />}
                <div className="border-chart mt-3">
                    {isDrill ? <DetailsNav key={`nav-${parentId}`} parent={parentId} onLoadParent={handleLoadParent} /> : ""}
                    {isLoaded ? <DetailsHeader key={`header-${chartId}-${isDrill}`} chartId={chartId} displayName={definition.display_name} onChartPicker={loadChartPicker} /> : ""}
                    {isLoaded ? <ChartDetails key={`details-${chartId}-${isDrill}`} parent={parentId} chartId={chartId} chart={chart} onDrill={handleDetermineDrill} /> : ""}
                    {isLoaded ? <ChartFooter key={`footer-${chartId}-${isDrill}`} showUpdate="true" drillText={drillText} updatedText={updatedText} /> : ""}
                </div>
                {isLoaded && <DataTableAccordion data={data} definition={definition} />}
                <div className="details-footer-padding"></div>
            </div>
        </div>
    );
};

export default DetailsLoader;
